import { useState, useEffect, useCallback } from 'react';
import { Gift, Loader2, MessageCircle, ChevronLeft, ChevronRight, Edit2, X, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api/config';

const BirthdaysPage = () => {
  const [birthdays, setBirthdays] = useState(() => {
    const cached = sessionStorage.getItem('birthdays_cache');
    return cached ? JSON.parse(cached) : [];
  });
  const [loading, setLoading] = useState(() => !sessionStorage.getItem('birthdays_cache'));
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const saved = localStorage.getItem('birthdays_per_page');
    return saved ? parseInt(saved, 10) : 10;
  });
  const [pagination, setPagination] = useState(() => {
    const cached = sessionStorage.getItem('birthdays_pagination');
    return cached ? JSON.parse(cached) : { total: 0, pages: 1 };
  });

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchBirthdays = useCallback(async () => {
    if (birthdays.length === 0) setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/applications.php?action=birthdays&page=${page}&limit=${perPage}`);
      const data = await res.json();
      if (data.success) {
        const list = data.data || [];
        setBirthdays(list);
        sessionStorage.setItem('birthdays_cache', JSON.stringify(list));
        const pag = data.pagination || { total: 0, pages: 1 };
        setPagination(pag);
        sessionStorage.setItem('birthdays_pagination', JSON.stringify(pag));
      } else {
        setError(data.error || 'Failed to fetch birthdays');
      }
    } catch (err) {
      setError('Failed to load birthdays');
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    fetchBirthdays();
  }, [fetchBirthdays]);

  const formatDOB = (dobStr) => {
    if (!dobStr) return '—';
    const date = new Date(dobStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const getCountdownText = (dobStr) => {
    if (!dobStr) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dob = new Date(dobStr);
    let nextBday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    
    // If birthday already passed this year, it will be next year
    if (nextBday < today) {
      nextBday.setFullYear(today.getFullYear() + 1);
    }
    
    const diffTime = nextBday.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today! 🎂';
    if (diffDays === 1) return 'Tomorrow';
    
    if (diffDays >= 30) {
      const months = Math.round(diffDays / 30);
      return `in ${months} month${months > 1 ? 's' : ''}`;
    }
    
    return `in ${diffDays} days`;
  };

  const navigate = useNavigate();

  const handleEditClick = (person) => {
    navigate('/dashboard/customers', { 
      state: { editApplicationId: person.application_id } 
    });
  };

  const handleSaveEdit = async () => {
    if (!editData.name || !editData.phone) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/applications.php?action=update&id=${editData.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section: 'personal',
          data: {
            client_name: editData.name,
            contact_no: editData.phone
          }
        })
      });
      const data = await res.json();
      if (data.success) {
        setEditModalOpen(false);
        fetchBirthdays(); // refresh list
      } else {
        alert(data.message || 'Failed to update');
      }
    } catch (err) {
      alert('Error updating data');
    } finally {
      setSaving(false);
    }
  };

  const openWhatsApp = (phone, name) => {
    let num = String(phone).replace(/\D/g, '');
    if (num.length === 10) num = '91' + num;
    const msg = encodeURIComponent(`*Happy Birthday ${name}!* Wishing you a fantastic day filled with joy and success! :)`);
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
  };

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-pink-100 rounded-lg text-pink-600">
            <Gift size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Upcoming Birthdays</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {pagination.total} total birthday{pagination.total !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-red-500">{error}</div>
        ) : birthdays.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <Gift size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">No upcoming birthdays found</p>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">#</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Name</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Contact</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap text-center">Birthday</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {birthdays.map((person, i) => (
                  <tr key={person.application_id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 font-medium">
                      {(page - 1) * perPage + i + 1}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {person.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {person.phone || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-100 text-pink-700 font-bold text-xs">
                          <Gift size={12} />
                          {formatDOB(person.dob)}
                        </span>
                        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                          {getCountdownText(person.dob)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEditClick(person)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg font-medium text-xs hover:bg-gray-200 transition-colors shadow-sm border border-gray-200"
                          title="Edit Name & Number"
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => openWhatsApp(person.phone, person.name)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white rounded-lg font-medium text-xs hover:bg-green-600 transition-colors shadow-sm"
                          title="Wish on WhatsApp"
                        >
                          <MessageCircle size={14} />
                          Wish
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex flex-wrap items-center justify-between px-4 py-3 border-t border-gray-100 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Rows per page:</span>
              <div className="flex flex-wrap gap-1">
                {[10, 20, 50, 100, 250, 500].map(n => (
                  <button
                    key={n}
                    onClick={() => { setPerPage(n); setPage(1); localStorage.setItem('birthdays_per_page', n); }}
                    className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                      perPage === n
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-500">
                Page {page} of {pagination.pages} ({pagination.total} records)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1"
                >
                  <ChevronLeft size={13} /> Prev
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1"
                >
                  Next <ChevronRight size={13} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Edit2 size={18} className="text-primary-500" />
                Edit Details
              </h3>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contact Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  value={editData.phone}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editData.name || !editData.phone}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthdaysPage;
